import { Action, Selector, State, StateContext } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { ReportControllerInsightPartnerService } from '@generated-apis/api/reportController-insightpartner.service';
import {
  GenerateReport,
  GetReportGenerationStatus,
  InitiatePolling,
  MonitoringReportDataLoaded,
  ResetReportGeneration,
  TriggerReportGeneration,
} from '@shared/redux/report-generation.actions';
import { EMPTY, switchMap, takeWhile, tap, throwError, timer } from 'rxjs';
import { SnackbarV3Service } from '@gea/digital-ui-lib';
import { catchError, finalize } from 'rxjs/operators';
import { showSnackbarError } from '@shared/utils/shared-helpers';
import { NETWORK_ERROR_MESSAGE, NETWORK_ERROR_TITLE } from '@shared/models/translations';
import { ReportStatus } from '@shared/models/common.models';
import { UserLocaleService } from '@shared/services/user-locale/user-locale.service';
import { HttpErrorResponse } from '@angular/common/http';
import { formatInTimeZone } from 'date-fns-tz';

export interface ReportGenerationStateModel {
  reportId: string | null;
  startDate: string | null;
  stopDate: string | null;
  pollingStatus: ReportStatus | null;
  geaPUID: string | null;
  serialNumber: string | null;
  generatedReport: Blob | null;
  reportGenerationLoading: boolean;
  reportStatus: ReportStatus | null;
  timezone: string | null;
  manufacturer: string | null;
  machineDisplayName: string | null;
  yearOfManufacturing: string | null;
}

const defaults: ReportGenerationStateModel = {
  reportId: null,
  startDate: null,
  stopDate: null,
  pollingStatus: null,
  geaPUID: null,
  serialNumber: null,
  generatedReport: null,
  reportGenerationLoading: false,
  reportStatus: null,
  timezone: null,
  manufacturer: null,
  machineDisplayName: null,
  yearOfManufacturing: null,
};

@State<ReportGenerationStateModel>({
  name: 'reportGeneration',
  defaults: defaults,
})
@Injectable()
export class ReportGenerationState {
  private readonly reportService = inject(ReportControllerInsightPartnerService);
  private readonly snackbarService = inject(SnackbarV3Service);
  private readonly userLocaleService = inject(UserLocaleService);

  @Selector()
  static pollingStatus(state: ReportGenerationStateModel) {
    return state.pollingStatus;
  }

  @Selector()
  static reportGenerationLoading(state: ReportGenerationStateModel) {
    return state.reportGenerationLoading;
  }

  @Selector()
  static reportStatus(state: ReportGenerationStateModel) {
    return state.reportStatus;
  }

  @Selector()
  static reportGeneralData(state: ReportGenerationStateModel): Partial<ReportGenerationStateModel> {
    return {
      timezone: state.timezone,
      serialNumber: state.serialNumber,
      manufacturer: state.manufacturer,
      machineDisplayName: state.machineDisplayName,
      yearOfManufacturing: state.yearOfManufacturing,
      reportId: state.reportId,
      startDate: state.startDate,
      stopDate: state.stopDate,
      reportStatus: state.reportStatus,
    };
  }

  @Action(TriggerReportGeneration)
  triggerReportGeneration(ctx: StateContext<ReportGenerationStateModel>, action: TriggerReportGeneration) {
    const dateRange = this.userLocaleService.convertDateRangeToUserTimeZone(action.dateRange);
    ctx.patchState({ reportGenerationLoading: true });

    return this.reportService
      .machineTriggerReport(action.geaPUID, {
        start: dateRange.startDate,
        stop: dateRange.endDate,
        timezone: this.userLocaleService.userTimezone(),
      })
      .pipe(
        tap((response) => {
          ctx.patchState({
            reportId: response.report_id,
            startDate: response.start,
            stopDate: response.stop,
            geaPUID: action.geaPUID,
            serialNumber: response.serialnumber,
          });
        }),
        catchError(() => {
          ctx.patchState({ reportId: null, startDate: null, stopDate: null });
          showSnackbarError(this.snackbarService, NETWORK_ERROR_TITLE, NETWORK_ERROR_MESSAGE);
          return EMPTY;
        })
      );
  }

  @Action(MonitoringReportDataLoaded)
  reportDataLoaded(ctx: StateContext<ReportGenerationStateModel>, action: MonitoringReportDataLoaded) {
    const start = action.report.start;
    const stop = action.report.stop;
    const timezone = action.report.timezone;

    const stateUpdate: Partial<ReportGenerationStateModel> = {
      reportId: action.report.report_id,
      serialNumber: action.report.serialnumber,
      manufacturer: action.report.manufacturer,
      machineDisplayName: action.report.machineDisplayName,
      yearOfManufacturing: action.report.yearOfManufacture,
      timezone: timezone,
    };


    if (start && stop && timezone) {
      stateUpdate.startDate = formatInTimeZone(start, timezone, 'yyyy-MM-dd');
      stateUpdate.stopDate = formatInTimeZone(stop, timezone, 'yyyy-MM-dd');
    }

    ctx.patchState(stateUpdate);
  }

  @Action(GenerateReport)
  generateReport(ctx: StateContext<ReportGenerationStateModel>, action: GenerateReport) {
    const geaPUID = action.geaPUID ?? ctx.getState().geaPUID;
    const timezone = action.timezone ?? this.userLocaleService.userTimezone();
    const startDate = action.startDate ?? ctx.getState().startDate?.split('T')[0];
    const endDate = action.endDate ?? ctx.getState().stopDate?.split('T')[0];
    const serialNumber = ctx.getState().serialNumber ?? '';

    if (!geaPUID || !timezone || !startDate || !endDate) {
      return throwError(() => {
        showSnackbarError(this.snackbarService, NETWORK_ERROR_TITLE, NETWORK_ERROR_MESSAGE);
        ctx.patchState({ generatedReport: null });
      });
    }

    return this.reportService
      .generateReport({
        geaPUID: geaPUID,
        timezone: timezone,
        startDate: startDate,
        endDate: endDate,
      })
      .pipe(
        tap((response) => {
          const mediaType = 'application/pdf';
          const blob = new Blob([response], { type: mediaType });
          ctx.patchState({ generatedReport: blob });

          const a = document.createElement('a');
          const objectUrl = URL.createObjectURL(blob);
          a.href = objectUrl;
          a.download = this.buildPdfName(serialNumber, geaPUID, startDate, endDate);
          a.click();
          URL.revokeObjectURL(objectUrl);
        }),
        catchError(() => {
          showSnackbarError(this.snackbarService, NETWORK_ERROR_TITLE, NETWORK_ERROR_MESSAGE);
          ctx.patchState({ generatedReport: null });
          return EMPTY;
        }),
        finalize(() => {
          ctx.patchState({ reportGenerationLoading: false });
        })
      );
  }

  @Action(InitiatePolling)
  initiatePolling(ctx: StateContext<ReportGenerationStateModel>, action: InitiatePolling) {
    return timer(0, action.pollingInterval).pipe(
      switchMap(() => this.reportService.getReportGeneratedResults(action.geaPUID)),
      takeWhile((response) => response.status === 'PENDING', true),
      tap((response) => {
        ctx.patchState({
          pollingStatus: response.status as ReportStatus,
          timezone: response.timezone,
          serialNumber: response.serialnumber,
          manufacturer: response.manufacturer,
          yearOfManufacturing: response.yearOfManufacture,
          startDate: response.start,
          stopDate: response.stop,
        });

        if ((response.status as ReportStatus) === 'FAILED') {
          ctx.patchState(defaults);
          showSnackbarError(this.snackbarService, NETWORK_ERROR_TITLE, NETWORK_ERROR_MESSAGE);
        }
      }),
      catchError(() => {
        ctx.patchState(defaults);
        showSnackbarError(this.snackbarService, NETWORK_ERROR_TITLE, NETWORK_ERROR_MESSAGE);
        return EMPTY;
      })
    );
  }

  @Action(GetReportGenerationStatus)
  getReportGenerationStatus(ctx: StateContext<ReportGenerationStateModel>, action: GetReportGenerationStatus) {
    return this.reportService.getReportStatus(action.geaPUID).pipe(
      tap((response) => {
        if (response.status === 'PENDING') {
          ctx.patchState({ reportStatus: response.status, geaPUID: action.geaPUID });
        }
      }),
      catchError((err: HttpErrorResponse) => {
        if (err.status === 404) {
          return EMPTY;
        }

        ctx.patchState(defaults);
        showSnackbarError(this.snackbarService, NETWORK_ERROR_TITLE, NETWORK_ERROR_MESSAGE);
        return EMPTY;
      })
    );
  }

  @Action(ResetReportGeneration)
  resetReportGeneration(ctx: StateContext<ReportGenerationStateModel>) {
    ctx.patchState(defaults);
  }

  private buildPdfName(serialNumber: string, geaPUID: string, startDate: string, endDate: string) {
    const serialNumberFormatted = serialNumber.length ? `${serialNumber}__` : '';

    return `${serialNumberFormatted}${geaPUID}__${startDate}__${endDate}__Report.pdf`;
  }
}
